import { useState } from 'react';
import styled from 'styled-components';
import { Text } from '../text';
import { Input } from '../numeric-input';
import { FromDisplayNumber, ToDisplayNumber } from 'src/Logic/Helpers/number-helpers';

const BalanceContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 100px;
    justify-content: center;
    position: relative;
    margin: 6px 0px;

    .balance {
        font-size: 16px;
        line-height: 20px;
        font-weight: normal;
        text-align: center;
        text-transform: capitalize;
    }

    .editIcon {
        cursor: pointer;
        font-size: 14px;
        user-select: none;
        opacity: 0.5;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    .resetIcon, .applyIcon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        font-size: 14px;
        user-select: none;
        opacity: 0.5;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }

        &.disabled {
            opacity: 0.2;
            cursor: not-allowed;
        }
    }

    .resetIcon {
        right: -38px;
    }

    .applyIcon {
        right: -20px;
    }

    .balanceInput {
        width: 90px;
        margin: 0;

        input {
            height: 24px;
            font-size: 14px;
            padding: 2px 6px;
            text-align: center;
            margin: 0;
        }

        span {
            display: none;
        }
    }
`;

type Props = {
    balance: string;
    canUpdate: boolean;
    onBalanceUpdate: (newBalance: number) => Promise<void>;
};

export const ParticipantBalance = ({ balance, canUpdate, onBalanceUpdate }: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [isError, setIsError] = useState(false);

    // Parse the balance string (e.g., "$10,000" -> 10000)
    const numericBalance = FromDisplayNumber(balance.replace('$', ''));

    const handleEditClick = () => {
        setIsEditing(true);
        setEditValue(ToDisplayNumber(numericBalance));
        setIsError(false);
    };

    const handleResetClick = () => {
        setIsEditing(false);
        setEditValue('');
        setIsError(false);
    };

    const handleApplyClick = async () => {
        const numericValue = FromDisplayNumber(editValue);

        // Validation
        if (editValue.trim() === '' || numericValue < 0 || numericValue % 1 !== 0) {
            setIsError(true);
            return;
        }

        setIsSaving(true);
        try {
            await onBalanceUpdate(numericValue);
            setIsEditing(false);
            setIsError(false);
        } catch (error) {
            // Error handling is done in the onBalanceUpdate function
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setEditValue(value);

        // Validate
        if (value.trim() === '') {
            setIsError(true);
        } else {
            const numericValue = FromDisplayNumber(value);
            if (numericValue < 0 || numericValue % 1 !== 0) {
                setIsError(true);
            } else {
                setIsError(false);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !isSaving && !isError) {
            handleApplyClick();
        }
    };

    if (!isEditing) {
        return (
            <BalanceContainer>
                <Text className="balance">{balance}</Text>
                {canUpdate && (
                    <span className="editIcon" onClick={handleEditClick}>
                        ✏️
                    </span>
                )}
            </BalanceContainer>
        );
    }

    return (
        <BalanceContainer>
            <Input
                className="balanceInput"
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                error={isError ? ' ' : undefined}
                autoFocus={true}
            />
            <span
                className={`resetIcon${isSaving ? ' disabled' : ''}`}
                onClick={isSaving ? undefined : handleResetClick}
            >
                ❌
            </span>
            <span
                className={`applyIcon${isSaving ? ' disabled' : ''}`}
                onClick={isSaving ? undefined : handleApplyClick}
            >
                ✅
            </span>
        </BalanceContainer>
    );
};
